// Token Management Utilities
// Handles automatic token refresh and persistent login

const TOKEN_KEY = 'firebaseIdToken';
const TOKEN_EXPIRATION_KEY = 'tokenExpiration';
const REFRESH_THRESHOLD = 7 * 24 * 60 * 60 * 1000; // 7 days before expiration

export class TokenManager {
  static setToken(token) {
    localStorage.setItem(TOKEN_KEY, token);
    
    // Set expiration to 30 days from now
    const expirationTime = Date.now() + (30 * 24 * 60 * 60 * 1000);
    localStorage.setItem(TOKEN_EXPIRATION_KEY, expirationTime.toString());
    
    console.log('Token set with 30-day expiration');
  }

  static getToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  static isTokenExpired() {
    const expirationTime = localStorage.getItem(TOKEN_EXPIRATION_KEY);
    if (!expirationTime) return true;
    
    return Date.now() > parseInt(expirationTime);
  }

  static shouldRefreshToken() {
    const expirationTime = localStorage.getItem(TOKEN_EXPIRATION_KEY);
    if (!expirationTime) return true;
    
    const timeUntilExpiration = parseInt(expirationTime) - Date.now();
    return timeUntilExpiration < REFRESH_THRESHOLD;
  }

  static clearToken() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRATION_KEY);
    console.log('Token cleared');
  }

  static getTimeUntilExpiration() {
    const expirationTime = localStorage.getItem(TOKEN_EXPIRATION_KEY);
    if (!expirationTime) return 0;
    
    return Math.max(0, parseInt(expirationTime) - Date.now());
  }

  static getDaysUntilExpiration() {
    const timeUntilExpiration = this.getTimeUntilExpiration();
    return Math.floor(timeUntilExpiration / (24 * 60 * 60 * 1000));
  }

  static async refreshTokenIfNeeded() {
    if (!this.shouldRefreshToken()) {
      return this.getToken();
    }

    try {
      const currentToken = this.getToken();
      if (!currentToken) return null;

      // Try to refresh token by making a request to validate endpoint
      const response = await fetch('https://empty-rain-2654.er-hooman.workers.dev/api/auth/me', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Token is still valid, extend expiration
        const newExpirationTime = Date.now() + (30 * 24 * 60 * 60 * 1000);
        localStorage.setItem(TOKEN_EXPIRATION_KEY, newExpirationTime.toString());
        console.log('Token expiration extended');
        return currentToken;
      } else {
        // Token is invalid, clear it
        this.clearToken();
        return null;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      return null;
    }
  }

  static setupAutoRefresh() {
    // Check token every hour
    setInterval(() => {
      this.refreshTokenIfNeeded();
    }, 60 * 60 * 1000); // 1 hour

    // Check token when page becomes visible
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.refreshTokenIfNeeded();
      }
    });

    // Check token when page loads
    window.addEventListener('load', () => {
      this.refreshTokenIfNeeded();
    });
  }
}

export default TokenManager;
